import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './Modal.css';

let openModals = 0;
export function Modal({ title, onClose, children, wide = false, fullscreen = false }: {
    title: string; onClose: () => void; children: React.ReactNode; wide?: boolean; fullscreen?: boolean;
}) {
    const panel = useRef<HTMLDivElement>(null);
    const close = useRef(onClose);
    close.current = onClose;
    const [full, setFull] = useState(false);
    const [notice, setNotice] = useState('');
    useEffect(() => {
        const previous = document.activeElement as HTMLElement | null;
        const root = document.getElementById('root');
        openModals++;
        document.body.classList.add('modal-open');
        root?.setAttribute('inert', '');
        panel.current?.focus();
        const keys = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !document.fullscreenElement) { e.preventDefault(); close.current(); }
            if (e.key !== 'Tab') return;
            const nodes = Array.from(panel.current?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input, textarea, select, [tabindex="0"]') || []).filter(n => n.getClientRects().length);
            const first = nodes[0], last = nodes[nodes.length - 1];
            if (!first) { e.preventDefault(); return; }
            if (e.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) { e.preventDefault(); last.focus(); }
            else if (!e.shiftKey && (document.activeElement === last || document.activeElement === panel.current)) { e.preventDefault(); first.focus(); }
        };
        const changed = () => setFull(!!document.fullscreenElement);
        document.addEventListener('keydown', keys);
        document.addEventListener('fullscreenchange', changed);
        return () => {
            document.removeEventListener('keydown', keys);
            document.removeEventListener('fullscreenchange', changed);
            if (--openModals === 0) { root?.removeAttribute('inert'); document.body.classList.remove('modal-open'); }
            if (document.fullscreenElement) document.exitFullscreen?.().catch(() => undefined);
            previous?.focus();
        };
    }, []);
    const toggleFullscreen = async () => {
        try {
            if (document.fullscreenElement) await document.exitFullscreen();
            else if (panel.current?.requestFullscreen) await panel.current.requestFullscreen();
            else setNotice('Your browser uses this full-window view. You can rotate your device while playing.');
        } catch { setNotice('Fullscreen is unavailable here. This view still works in either orientation.'); }
    };
    return createPortal(<div className={`sparkle-modal-backdrop ${wide ? 'is-wide' : ''}`}>
        <div ref={panel} className="sparkle-modal" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1}>
            <header className="sparkle-modal-header"><h2>{title}</h2><div className="modal-header-actions">
                {fullscreen && <button onClick={toggleFullscreen}>{full ? 'Exit fullscreen' : 'Fullscreen'}</button>}
                <button onClick={onClose} aria-label={`Close ${title}`}>Close</button>
            </div></header>
            {notice && <p className="inline-notice" role="status">{notice}</p>}
            <div className="sparkle-modal-body">{children}</div>
        </div>
    </div>, document.body);
}
